import axios from 'axios';
import { EP_BASE_API, EP_AUTH_REFRESH, EP_AUTH_LOGIN, EP_AUTH_PUBLIC_CONFIG } from '../constants/endPointConstants';
import { getAccessToken, getRefreshToken, clearTokens } from './AuthSession';

import { refreshSession } from './SessionRefresh';
import { getRoomMembershipVersion, getRoomCredential, roomIdFromRequest } from './RoomSession';
import SignalRService from './SignalRService';

const api = axios.create({
    baseURL: EP_BASE_API,
    headers: {
        'Content-Type': 'application/json',
    },
});

api.interceptors.request.use((config) => {
    if (config._anonymous) {
        if (config.headers) delete config.headers.Authorization;
        return config;
    }
    if (/\/api\/(action|chat)\//i.test(config.url || '') && (config.method || 'get').toLowerCase() !== 'get' && !SignalRService.isReady()) {
        return Promise.reject(new Error('CONNECTION_UNAVAILABLE'));
    }
    const roomId = roomIdFromRequest(config.url) || config._roomId;
    if (roomId) {
        config.headers = config.headers || {};
        // A token-refresh retry must keep the membership that issued the
        // original action, not acquire permission from a later rejoin.
        if (config.headers['X-Room-Membership-Version'] == null)
            config.headers['X-Room-Membership-Version'] = getRoomMembershipVersion(roomId);
    }
    const credential = config.headers?.['X-PWF-Room-Credential'] || getRoomCredential(roomId);
    if (credential) {
        config.headers = config.headers || {};
        config.headers['X-PWF-Room-Credential'] = credential;
        delete config.headers.Authorization;
        return config;
    }
    const token = getAccessToken();
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

const isAuthUrl = (url = '') =>
    url.includes(EP_AUTH_LOGIN) ||
    url.includes(EP_AUTH_REFRESH) ||
    url.includes(EP_AUTH_PUBLIC_CONFIG);

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error.config || {};
        const status = error?.response?.status;
        const roomId = roomIdFromRequest(original.url) || original._roomId;
        if (roomId && String(original.headers?.['X-Room-Membership-Version'] ?? '') === getRoomMembershipVersion(roomId) &&
            ['ROOM_MEMBERSHIP_ENDED', 'STALE_ROOM_MEMBERSHIP', 'ROOM_NOT_FOUND'].includes(error?.response?.data?.code)) {
            window.dispatchEvent(new CustomEvent('pwf-room-membership-ended', { detail: { roomId } }));
        }
        if (status === 401 && !original.headers?.['X-PWF-Room-Credential'] && !original._retry && !isAuthUrl(original.url)) {
            original._retry = true;
            const tokenResponse = await refreshSession();
            const newToken = tokenResponse?.accessToken;
            if (newToken) {
                original.headers = original.headers || {};
                original.headers.Authorization = `Bearer ${newToken}`;
                return api(original);
            }
            // A concurrent login can supersede the request being refreshed.
            if (getRefreshToken()) return Promise.reject(error);
            // Only a confirmed invalid/missing refresh session reaches here.
            // Transient refresh failures reject above without clearing tokens.
            clearTokens();
            if (window.location.pathname !== '/login' && !/^\/play\//.test(window.location.pathname)) {
                window.location.assign('/login');
            }
        }
        return Promise.reject(error);
    }
);

export default api;
